import React, { useState } from "react";
import { useRouteMatch } from "react-router-dom";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import clsx from "clsx";

import useMediaQuery from "@material-ui/core/useMediaQuery";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { MuiTheme } from "theme";

import ErrorBoundary from "components/ErrorBoundary";
import HorseWorkoutInfo from "components/HorseWorkoutInfo";
import Loader from "components/Loader";
import AppPage from "components/AppPage";
import AppPageHeader from "components/AppPageHeader";
import AppPageContent from "components/AppPageContent";
import BackLink from "components/BackLink";
import { useRacehorse360Api } from "hooks/api";
import Breakpoints from "common/breakpoints";
import routes from "common/routes";
import HorseWorkoutComments from "./HorseWorkoutComments";
import useStyles from "./styles";

const HorseWorkoutCommentsPage = () => {
  const match = useRouteMatch<{ workoutId: string }>(
    routes.workoutComments.path
  );

  const matchesDownXS420 = useMediaQuery((theme: MuiTheme) =>
    theme.breakpoints.down(Breakpoints.XS_420)
  );
  const classes = useStyles();
  const workoutId: string = match.params?.workoutId;
  const [edit, setEdit] = useState<string | number>(null);

  const { useGetWorkoutRequest } = useRacehorse360Api();

  const { isLoading: isWorkoutRequestLoading, data: workoutInfo } =
    useGetWorkoutRequest(
      {
        id: workoutId,
        getOptions: {
          select: [
            "date",
            "horse.name",
            "horse.lastRaceDate",
            "horse.lastRaceFacility",
            "horse.lastRaceFacility.name",
            "horse.lastWorkoutDate",
            "horse.lastWorkoutFacility.name",
            "horse.lastWorkoutFacility",
            "horse.currentFacility.name",
            "horse.currentFacility.code",
            "horse.barn.name",
            "horse.barn.facility.name",
            "horse.barn.facility.code",
            "facility.name",
            "facility.code"
          ]
        }
      },
      {
        enabled: Boolean(workoutId)
      }
    );

  const handleEditStart = (commentId: string | number) => {
    setEdit(commentId);
  };

  const handleEditFinish = () => {
    setEdit(null);
  };

  return (
    <>
      {isWorkoutRequestLoading ? (
        <Loader />
      ) : (
        <AppPage className={classes.appPage}>
          <AppPageHeader className={classes.appPageHeader}>
            <ErrorBoundary>
              <Box className={classes.topBar}>
                <Box
                  className={clsx(classes.topBarLine, classes.topBarTitleLine)}
                >
                  <BackLink className={classes.arrowBackLink} />
                  <Typography component={"b"} className={classes.topBarTitle}>
                    Workout Comments
                  </Typography>
                </Box>
                <Divider />
                <Box className={classes.topBarSubtitleLine}>
                  <Box className={classes.topBarSubTitleLeft}>
                    <Typography
                      className={clsx(classes.topBarSubTitle, classes.hideOnSm)}
                    >
                      Commenting on your workout with&nbsp;
                    </Typography>
                    <Typography
                      className={classes.topBarSubTitleName}
                      component="b"
                    >
                      {workoutInfo.horse.name}
                    </Typography>
                  </Box>
                  <Box className={classes.topBarSubTitleRight}>
                    <Typography
                      className={clsx(classes.topBarSubTitle, classes.hideOnSm)}
                    >
                      &nbsp;scheduled for&nbsp;
                    </Typography>
                    <Typography
                      className={classes.topBarSubTitleDate}
                      component="b"
                    >
                      {format(
                        parseISO(workoutInfo?.date),
                        matchesDownXS420 ? "iii, MMM dd" : "iiii, MMMM dd"
                      )}
                    </Typography>
                    <Typography
                      className={clsx(classes.topBarSubTitle, classes.hideOnSm)}
                    >
                      &nbsp;at&nbsp;
                    </Typography>
                    <Typography
                      className={classes.topBarSubTitleFacility}
                      component="b"
                    >
                      {matchesDownXS420
                        ? workoutInfo.facility.code
                        : workoutInfo.facility.name}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </ErrorBoundary>
          </AppPageHeader>

          <AppPageContent>
            <Container
              maxWidth={false}
              className={clsx(classes.component, {
                [classes.progress]: isWorkoutRequestLoading,
                [classes.editMode]: edit
              })}
            >
              <Grid className={classes.grid} container>
                <Grid
                  className={clsx(classes.column, {
                    [classes.hiddenOnSm]: edit
                  })}
                  item
                  md={4}
                  lg={5}
                >
                  <ErrorBoundary>
                    <HorseWorkoutInfo horse={workoutInfo.horse} />
                  </ErrorBoundary>
                </Grid>
                <Grid
                  className={classes.column}
                  item
                  xs={12}
                  sm={12}
                  md={edit ? 12 : 8}
                  lg={7}
                >
                  <ErrorBoundary>
                    <HorseWorkoutComments
                      onEditStart={handleEditStart}
                      onEditFinish={handleEditFinish}
                      workout={workoutInfo}
                    />
                  </ErrorBoundary>
                </Grid>
              </Grid>
            </Container>
          </AppPageContent>
        </AppPage>
      )}
    </>
  );
};

export default HorseWorkoutCommentsPage;
